/**
 * Submit Preparation Request — the Adviser's notebook-style submission form.
 *
 * A preparation request is a "notebook" the Adviser fills in before a client
 * appointment, listing the tasks the New Business Assistant must complete.
 * On submit it inserts one row into preparation_requests with status
 * 'submitted' (see CLAUDE.md §25/§26). A successful submit returns the user
 * to their dashboard with the new card highlighted in Inbox (§39).
 *
 * Adviser-only (gated by AdviserOnlyRoute in App.jsx). Rendered inside the
 * standard AppShell, so the header (role pill) and footer come from the app
 * chrome; only the inner notebook adopts the paper aesthetic.
 *
 * The notebook surface, fonts and field styling come from the shared
 * NotebookPaper primitives so the form and the read-only detail view stay in
 * lock-step.
 */

import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AlertCircle, Loader } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { getRoleLabel } from '../auth/authConfig';
import { createPrepRequest } from '../api/prepRequestsApi';
import { createSystemNote } from '../api/prepNotesApi';
import { updateReviewClientsExpanded } from '../api/reviewClientsApi';
import { uploadVoiceNote } from '../api/storageApi';
import VoiceRecorder from '../components/recorder/VoiceRecorder';
import { previousBusinessDay } from '../utils/businessDays';
import { getTodaySASTDateString } from '../utils/dateHelpers';
import { formatPrepDeadlineWeekday } from '../utils/prepRequests';
import { returnToDashboard } from '../utils/returnToDashboard';
import { NotebookPage, nb, notebookCss } from '../components/prep/NotebookPaper';
import PrepTaskListEditor from '../components/prep/PrepTaskListEditor';
import { newPrepTask, normalisePrepTasks, dropBlankTasks } from '../utils/prepTasks';

// First-name lookup for the creation system note — mirrors the local
// EMAIL_TO_FIRST_NAME convention in PrepRequestDetail / FileDetail.
const EMAIL_TO_FIRST_NAME = {
  'marinus@optimumgroup.co.za': 'Marinus',
  'wlourns@gmail.com': 'Wouter',
};
function firstNameOf(email) {
  return EMAIL_TO_FIRST_NAME[(email || '').toLowerCase()] || (email ? email.split('@')[0] : 'User');
}

// Format a previous-business-day Date (pinned to 12:00 UTC) as
// "→ Deadline: DD/MM/YYYY (Tue)" using its UTC parts so the calendar date is
// stable. Matches the mockup's abbreviated weekday in brackets.
function formatDeadline(date) {
  if (!date) return null;
  return `→ Deadline: ${formatPrepDeadlineWeekday(date)}`;
}

// Format a 'YYYY-MM-DD' string as DD/MM/YYYY for the read-only summary.
export default function SubmitPrepRequest() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const today = getTodaySASTDateString();

  // Phase 3 review handoff (§34.11): when opened from a single-client
  // calendar card, the form arrives pre-filled with the client's natural
  // display name and carries the review row's id so the created request
  // can be linked back. Everything else about the flow is the ordinary
  // prep submission experience.
  const sourceReviewClientId = location.state?.sourceReviewClientId || null;

  const [clientName, setClientName] = useState(location.state?.prefillClientName || '');
  const [clientLanguage, setClientLanguage] = useState('English');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [priority, setPriority] = useState('normal');
  // "Load as test file" — Adviser-only marker (migration 23). The route is
  // already Adviser-gated, but the checkbox is additionally gated on the
  // ACTIVE role and forced false in the submit handler for non-Advisers.
  const [isTest, setIsTest] = useState(false);
  // Task list — the two-level model in src/utils/prepTasks.js, edited by
  // the shared PrepTaskListEditor. Starts with one blank top-level line.
  const [tasks, setTasks] = useState(() => [newPrepTask()]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Deadline = appointment date minus one SA business day. Recomputed live.
  const deadline = useMemo(
    () => (appointmentDate ? previousBusinessDay(appointmentDate) : null),
    [appointmentDate]
  );
  const deadlineText = formatDeadline(deadline);
  // Tight-turnaround warning: deadline on or before today (SAST).
  const deadlineTight = useMemo(() => {
    if (!deadline) return false;
    const key = `${deadline.getUTCFullYear()}-${String(deadline.getUTCMonth() + 1).padStart(2, '0')}-${String(deadline.getUTCDate()).padStart(2, '0')}`;
    return key <= today;
  }, [deadline, today]);

  const assistantLabel = getRoleLabel('new_business_assistant');

  // ---- Voice note (CLAUDE.md §26.6) ----------------------------------------
  // Optional. The recording is attached to the request AS-IS for the New
  // Business Assistant to play back. Nothing is transcribed and no tasks are
  // drafted from it (the Whisper + task-extraction pipeline was removed on
  // 29 September 2026): the adviser types every task line by hand. The
  // recorder hands back null when the adviser re-records or deletes.
  const [voiceBlob, setVoiceBlob] = useState(null);
  const handleRecordingComplete = (blob) => setVoiceBlob(blob || null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const name = clientName.trim();
    if (!name) {
      setError('Client name is required.');
      return;
    }
    if (!appointmentDate) {
      setError('Please choose an appointment date.');
      return;
    }
    if (appointmentDate < today) {
      setError('The appointment date must be today or in the future.');
      return;
    }
    // Strip blank rows (a blank parent's sub-tasks are kept, promoted), then
    // run the ordering-invariant safety net; require at least one real task
    // (unchanged rule — the group review handoff is the only path that
    // creates an empty list, §34.11).
    const cleanTasks = normalisePrepTasks(dropBlankTasks(tasks));
    if (cleanTasks.length === 0) {
      setError('Add at least one task for the New Business Assistant.');
      return;
    }

    // Stored item shape: { id, text, completed, parent } (§25.2).
    const payloadTasks = cleanTasks.map((t) => ({
      id: t.id,
      text: t.text,
      completed: false,
      parent: t.parent || null,
    }));

    // Defensive: only the Adviser may mark a submission as a test record,
    // regardless of any UI state (the checkbox is only rendered for the
    // Adviser, but the role check is re-applied here as a safety net).
    const isTestSubmission = role === 'adviser' && isTest;
    // Test records carry a "TEST — " name prefix so they are recognisable
    // everywhere, unless the adviser already typed one starting "TEST".
    const submittedClientName =
      isTestSubmission && !name.startsWith('TEST') ? `TEST — ${name}` : name;

    try {
      setSubmitting(true);

      // Upload the recording only once the adviser actually submits, so a
      // cancelled form leaves nothing behind. Same bucket and helper client
      // file voice notes use; the key mirrors the review-handoff modal's
      // `prep-` prefix (prep rows get their id server-side).
      let voiceNoteUrl = null;
      if (voiceBlob) {
        voiceNoteUrl = await uploadVoiceNote(`prep-${crypto.randomUUID()}`, voiceBlob);
      }

      const created = await createPrepRequest({
        clientName: submittedClientName,
        appointmentDate,
        tasks: payloadTasks,
        createdBy: user?.email,
        clientLanguagePreference: clientLanguage,
        priority,
        isTest: isTestSubmission,
        // Review handoff linkage (§34.11) — null on ordinary submissions.
        sourceReviewClientId,
        // Voice note (§26.6) — the recording Vicky plays back. No
        // transcript and no summary are produced or stored.
        voiceNoteUrl,
      });
      // Creation audit note (CLAUDE.md 26.1): names WHO loaded the request —
      // the originator is now either the Adviser or the Administration
      // Assistant, and the sheet/notifications must never assume the
      // Adviser. Non-blocking: a note failure never affects the request.
      try {
        await createSystemNote(created.id, `Preparation request loaded by ${firstNameOf(user?.email)}.`);
      } catch (noteErr) {
        console.warn('[prep-create] creation system note failed:', noteErr);
      }

      // Link the review row to its ONE prep request (group-expanding
      // API, though a single-client handoff has no group). A linkage
      // failure never fails the submission — the request itself exists.
      if (sourceReviewClientId) {
        try {
          await updateReviewClientsExpanded([sourceReviewClientId], {
            prepRequestId: created.id,
          });
        } catch (linkErr) {
          console.warn('[review-handoff] prep created but linkage write failed:', linkErr);
        }
      }
      // Whole chain resolved (voice upload, record, creation note, review
      // linkage): return to the dashboard with the new card highlighted in
      // Inbox (CLAUDE.md §39). `replace` so browser back does not reopen
      // the completed form. A failure above never reaches this line.
      returnToDashboard(navigate, {
        role,
        cardType: 'prep',
        cardId: created.id,
        message: 'Preparation request submitted',
        replace: true,
      });
    } catch (err) {
      // Keep the entered data so nothing is lost; just surface the error.
      setError(err.message || 'Something went wrong saving the preparation request.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- The editable notebook form --------------------------------------
  return (
    <div style={styles.outer}>
      <style>{notebookCss}</style>

      <div style={styles.sectionHead}>
        <div style={styles.eyebrow}>{getRoleLabel(role)} submission form</div>
        <h2 style={styles.sectionTitle}>New Preparation Request</h2>
        <p style={styles.sectionHint}>
          A page from your notebook — jot the instructions for the {assistantLabel} to
          prepare a file ahead of the appointment.
        </p>
      </div>

      {error && (
        <motion.div
          style={styles.errorBanner}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <AlertCircle size={16} />
          <span>{error}</span>
        </motion.div>
      )}

      <form onSubmit={handleSubmit}>
        <NotebookPage>
          {/* Client name */}
          <div style={nb.fieldRow}>
            <label htmlFor="prep-client" style={nb.label}>Client Name</label>
            <input
              id="prep-client"
              className="np-input np-value"
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="e.g. Van der Merwe"
              autoComplete="off"
            />
          </div>

          {/* Client Language Preference */}
          <div style={nb.fieldRow}>
            <label htmlFor="prep-language" style={nb.label}>Client Language</label>
            <select
              id="prep-language"
              className="np-select"
              value={clientLanguage}
              onChange={(e) => setClientLanguage(e.target.value)}
            >
              <option value="English">English</option>
              <option value="Afrikaans">Afrikaans</option>
            </select>
          </div>

          {/* Appointment date + live deadline */}
          <div style={nb.fieldRow}>
            <label htmlFor="prep-date" style={nb.label}>Appointment date</label>
            <input
              id="prep-date"
              className="np-input np-date"
              type="date"
              value={appointmentDate}
              min={today}
              onChange={(e) => setAppointmentDate(e.target.value)}
            />
            {deadlineText && (
              <span style={{ ...nb.deadline, ...(deadlineTight ? nb.deadlineTight : {}) }}>
                {deadlineText}{deadlineTight ? ' ⚠' : ''}
              </span>
            )}
          </div>

          {/* Priority */}
          <div style={nb.fieldRow}>
            <span style={nb.label}>Priority</span>
            <button
              type="button"
              className={`np-choice${priority === 'normal' ? ' is-selected' : ''}`}
              onClick={() => setPriority('normal')}
              aria-pressed={priority === 'normal'}
            >
              Normal
            </button>
            <button
              type="button"
              className={`np-choice urgent${priority === 'urgent' ? ' is-selected' : ''}`}
              onClick={() => setPriority('urgent')}
              aria-pressed={priority === 'urgent'}
            >
              Urgent
            </button>
          </div>

          {/* Voice Note (§26.6) — optional; attached as-is for the New
              Business Assistant to play back. No transcription, no summary,
              no drafted tasks: the task lines below are typed by hand. */}
          <div style={{ ...nb.fieldRow, display: 'block' }}>
            <span style={{ ...nb.label, display: 'block', marginBottom: '4px' }}>
              Voice Note
            </span>
            <p style={styles.notebookHint}>
              Optional — attached to the request for {assistantLabel} to play back.
              Type the tasks below.
            </p>
            <VoiceRecorder
              onRecordingComplete={handleRecordingComplete}
              disabled={submitting}
            />
          </div>

          {/* Divider */}
          <div style={nb.divider}>Tasks in this file</div>
          <p style={styles.notebookHint}>
            What needs to be prepared or gathered for the appointment — one task per line.
            Tab makes a line a sub-task (1.a, 1.b …), Shift+Tab brings it back.
          </p>

          {/* Task list — shared two-level editor (tasks + sub-tasks). */}
          <PrepTaskListEditor tasks={tasks} onChange={setTasks} disabled={submitting} />

          {/* Attachments — reserved slot (Prompt 2) */}

          {/* Load as test file — Adviser only, and the LAST field on the
              form: it sits immediately above the action buttons
              (CLAUDE.md §33.2). The route is open to the Adviser and the
              Administration Assistant (§26.1), so the checkbox is gated on
              the ACTIVE role; the submit handler forces isTest to false for
              non-Advisers as a safety net. */}
          {role === 'adviser' && (
            <div style={{ ...nb.fieldRow, display: 'block' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={isTest}
                  onChange={(e) => setIsTest(e.target.checked)}
                  style={{ width: 'auto', cursor: 'pointer' }}
                  disabled={submitting}
                />
                <span style={nb.label}>Load as test file</span>
              </label>
              <p style={{ ...styles.notebookHint, margin: '4px 0 0' }}>
                Test files are excluded from stats and pipeline rules, and can be deleted later. Visible to Adviser only.
              </p>
            </div>
          )}
        </NotebookPage>

        <div style={styles.formActions}>
          <button className="btn btn-secondary" type="button" onClick={() => navigate('/')}>
            Cancel
          </button>
          <button className="btn btn-primary" type="submit" disabled={submitting}>
            {submitting ? <><Loader size={16} className="spin" /> Submitting…</> : 'Submit Preparation Request to Vicky'}
          </button>
        </div>
      </form>
    </div>
  );
}

const styles = {
  outer: {
    maxWidth: '720px',
    margin: '0 auto',
    padding: '0.5rem 0 2rem',
  },
  notebookHint: {
    fontFamily: "'Kalam', cursive",
    fontSize: '12px',
    color: '#6B7A8D',
    fontStyle: 'italic',
    margin: '0 0 8px',
  },
  sectionHead: {
    marginBottom: '1.25rem',
  },
  eyebrow: {
    fontSize: '11px',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    color: '#6B7A8D',
    marginBottom: '6px',
  },
  sectionTitle: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#0F4C75',
    margin: 0,
  },
  sectionHint: {
    fontSize: '14px',
    color: '#6B7A8D',
    lineHeight: 1.55,
    margin: '8px 0 0',
    maxWidth: '560px',
  },
  errorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'rgba(231, 111, 81, 0.12)',
    border: '1px solid var(--cp-warning)',
    color: '#C0392B',
    borderRadius: '10px',
    padding: '0.6rem 0.9rem',
    fontSize: '0.85rem',
    marginBottom: '1rem',
  },
  confirmBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    background: 'rgba(46, 196, 182, 0.14)',
    border: '1px solid var(--cp-success)',
    color: '#0F4C75',
    borderRadius: '10px',
    padding: '0.7rem 1rem',
    fontSize: '0.9rem',
    marginBottom: '1.25rem',
  },
  formActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '26px',
    padding: '0 12px',
    gap: '0.75rem',
    flexWrap: 'wrap',
  },
};
